import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Paper,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import { format } from 'date-fns';
import { sr } from 'date-fns/locale';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useAuth } from '../contexts/AuthContext';
import { notifyMessageSent } from '../utils/messageEvents';
import { C } from '../theme';

interface Conversation {
  id: number;
  ad_title: string;
  ad_id: number;
  business_ad_id: number | null;
  ad_type: 'land' | 'business' | 'unknown';
  sender_id: number;
  receiver_id: number;
  sender_name: string;
  receiver_name: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface Message {
  id: number;
  conversation_id: number;
  sender_id: number;
  content: string;
  created_at: string;
}

const Messages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [showInitialMessageModal, setShowInitialMessageModal] = useState<boolean>(false);
  const [initialMessageParams, setInitialMessageParams] = useState<{ adId: string; receiverId: string } | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSuccess, setReportSuccess] = useState('');
  const [reportError, setReportError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUnreadCount } = useUnreadMessages();
  const { user, isAuthenticated } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Redirect non-authenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchConversations();
  }, [isAuthenticated, navigate]);

  const fetchMessages = useCallback(async (conversationId: number): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<Message[]>(`/api/messages/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      refreshUnreadCount();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle query params for adId and receiverId
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const adId = params.get('adId');
    const receiverId = params.get('receiverId');
    const sendInitial = params.get('sendInitial');
    
    if (adId && receiverId && user?.id && sendInitial === '1') {
      // Check if a conversation already exists for this ad between these users
      let found = conversations.find(
        c => {
          const isSameAd = (c.ad_id && String(c.ad_id) === adId) || 
                         (c.business_ad_id && String(c.business_ad_id) === adId);
          const isSameUsers = (String(c.sender_id) === String(user.id) && String(c.receiver_id) === receiverId) ||
                             (String(c.receiver_id) === String(user.id) && String(c.sender_id) === receiverId);
          return isSameAd && isSameUsers;
        }
      );
      
      if (found) {
        // Conversation already exists, just select it
        setSelectedConversation(found);
        // Remove sendInitial from URL
        params.delete('sendInitial');
        navigate({ search: params.toString() }, { replace: true });
      } else {
        // Show the initial message modal
        setInitialMessageParams({ adId, receiverId });
        setShowInitialMessageModal(true);
      }
    }
  }, [location.search, user?.id, conversations, navigate]);

  const fetchConversations = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<Conversation[]>('/api/messages/conversations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };



  const handleSendMessage = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/messages/conversations/${selectedConversation.id}/messages`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations(); // Refresh conversations to update last message
      notifyMessageSent(); // Notify other components to update badge
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSendInitialMessage = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newMessage.trim() || !initialMessageParams) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/messages/conversations', {
        adId: Number(initialMessageParams.adId),
        receiverId: Number(initialMessageParams.receiverId),
        message: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear the message and refresh conversations
      setNewMessage('');
      await fetchConversations();
      
      // Close modal and clean up
      setShowInitialMessageModal(false);
      setInitialMessageParams(null);
      
      // Remove sendInitial from URL
      const params = new URLSearchParams(location.search);
      params.delete('sendInitial');
      navigate({ search: params.toString() }, { replace: true });
      
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: sr });
  };

  const getCurrentUserId = (): number | null => {
    return user?.id ?? null;
  };

  const isOwnMessage = (message: Message): boolean => {
    const currentUserId = getCurrentUserId();
    return currentUserId ? message.sender_id === currentUserId : false;
  };

  const getOtherUserName = (conversation: Conversation): string => {
    const currentUserId = getCurrentUserId();
    return currentUserId && conversation.sender_id === currentUserId
      ? conversation.receiver_name
      : conversation.sender_name;
  };

  const handleCloseInitialMessageModal = () => {
    setShowInitialMessageModal(false);
    setInitialMessageParams(null);
    setNewMessage('');
    
    // Remove sendInitial from URL
    const params = new URLSearchParams(location.search);
    params.delete('sendInitial');
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleReportUser = () => {
    setReportDialogOpen(true);
    setReportReason('');
    setReportSuccess('');
    setReportError('');
  };

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) {
      setReportError('Molimo unesite razlog prijave');
      return;
    }

    if (!selectedConversation) return;

    const otherUserId = selectedConversation.sender_id === user?.id 
      ? selectedConversation.receiver_id 
      : selectedConversation.sender_id;

    setReportLoading(true);
    setReportError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/moderation/report',
        {
          content_type: 'user',
          content_id: null,
          reported_user_id: otherUserId,
          reason: reportReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReportSuccess('Korisnik je uspešno prijavljen. Naš tim će pregledati prijavu u roku od 24 sata.');
      setReportReason('');
      setTimeout(() => {
        setReportDialogOpen(false);
        setReportSuccess('');
      }, 3000);
    } catch (err: any) {
      setReportError(err.response?.data?.message || 'Greška pri prijavljivanju korisnika');
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: '90px', pb: 6, minHeight: '100vh' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 800, color: C.white, mb: 3 }}>
        Poruke
      </Typography>
      
      <Grid container spacing={2}>
        {/* Conversations List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <List>
              {conversations.map((conversation) => {
                const isUnread = conversation.unread_count > 0;
                return (
                  <ListItem
                    key={conversation.id}
                    button
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{
                      backgroundColor: isUnread ? 'rgba(255,255,255,0.05)' : 'transparent',
                      borderLeft: isUnread ? '4px solid #F3C015' : '4px solid transparent',
                      '&:hover': {
                        backgroundColor: isUnread ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.05)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: isUnread ? 'rgba(243,192,21,0.10)' : 'rgba(255,255,255,0.07)',
                        '&:hover': {
                          backgroundColor: isUnread ? 'rgba(243,192,21,0.14)' : 'rgba(243,192,21,0.10)',
                        },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        backgroundColor: isUnread ? C.gold : C.surface2,
                        color: isUnread ? C.onGold : C.white,
                        fontWeight: isUnread ? 700 : 400,
                      }}>
                        {conversation.ad_title[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography 
                            variant="subtitle1" 
                            fontWeight={isUnread ? 800 : 700}
                            component="a"
                                                         href={conversation.ad_type === 'business' ? `/business/${conversation.ad_id}` : `/ads/${conversation.ad_id}`}
                            sx={{ 
                              color: isUnread ? C.gold : 'text.primary',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {conversation.ad_title}
                          </Typography>
                          {isUnread && (
                            <Box
                              sx={{
                                backgroundColor: C.gold,
                                color: C.onGold,
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                minWidth: 20,
                              }}
                            >
                              {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                            </Box>
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.primary" 
                            fontWeight={isUnread ? 700 : 600}
                            sx={{ 
                              color: isUnread ? C.gold : 'text.primary',
                            }}
                          >
                            {getOtherUserName(conversation)}
                          </Typography>
                          {' — '}
                          <Typography 
                            component="span" 
                            variant="body2" 
                            fontWeight={isUnread ? 600 : 400}
                            sx={{ 
                              color: isUnread ? C.gold : 'text.secondary',
                            }}
                          >
                            {conversation.last_message}
                          </Typography>
                          <br />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: isUnread ? C.gold : 'text.secondary',
                              fontWeight: isUnread ? 600 : 400,
                            }}
                          >
                            {formatDate(conversation.last_message_time)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Messages Area */}
        <Grid item xs={12} md={8}>
          {selectedConversation ? (
            <Paper sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
              {/* Messages Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={700}
                    component="a"
                                         href={selectedConversation.ad_type === 'business' ? `/business/${selectedConversation.business_ad_id}` : `/ads/${selectedConversation.ad_id}`}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {selectedConversation.ad_title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Razgovor sa {getOtherUserName(selectedConversation)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FlagIcon />}
                    onClick={handleReportUser}
                    sx={{
                      borderColor: '#F3C015',
                      color: '#F3C015',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#F3C015',
                        color: C.onGold,
                        borderColor: '#F3C015',
                      },
                    }}
                  >
                    Prijavi
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                                       onClick={() => navigate(selectedConversation.ad_type === 'business' ? `/business/${selectedConversation.ad_id}` : `/ads/${selectedConversation.ad_id}`)}
                    sx={{
                      borderColor: 'rgba(243,192,21,0.55)',
                      color: '#F6CB4B',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': {
                        background: C.goldGrad,
                        color: C.onGold,
                        borderColor: 'rgba(243,192,21,0.55)',
                      },
                    }}
                  >
                    Pogledaj oglas
                  </Button>
                </Box>
              </Box>

              {/* Messages List */}
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwnMessage(message) ? 'flex-end' : 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        backgroundColor: isOwnMessage(message) ? 'rgba(243,192,21,0.16)' : C.surface2,
                        color: isOwnMessage(message) ? '#fff' : 'inherit',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color={isOwnMessage(message) ? '#fff' : 'text.primary'}>
                        {isOwnMessage(message) ? 'Vi' : (selectedConversation.sender_id === message.sender_id ? selectedConversation.sender_name : selectedConversation.receiver_name)}
                      </Typography>
                      <Typography variant="body1" sx={{ color: isOwnMessage(message) ? '#fff' : 'inherit' }}>{message.content}</Typography>
                      <Typography variant="caption" color={isOwnMessage(message) ? 'rgba(255,255,255,0.55)' : 'text.secondary'}>
                        {formatDate(message.created_at)}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box
                component="form"
                onSubmit={handleSendMessage}
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Unesite poruku..."
                  value={newMessage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewMessage(e.target.value)}
                  size="small"
                />
                <IconButton
                  type="submit"
                  color="primary"
                  disabled={!newMessage.trim()}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                height: 'calc(100vh - 200px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Izaberite razgovor za prikaz poruka
              </Typography>
            </Paper>
          )}
         </Grid>
       </Grid>
       
       {/* Initial Message Modal */}
       <Dialog 
         open={showInitialMessageModal} 
         onClose={handleCloseInitialMessageModal}
         maxWidth="sm"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
           }
         }}
       >
         <DialogTitle sx={{ pb: 1 }}>
           <Typography variant="h6" fontWeight={600}>
             Pošaljite poruku vlasniku oglasa
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
             Ova poruka će započeti novi razgovor sa vlasnikom oglasa.
           </Typography>
         </DialogTitle>
         
         <DialogContent sx={{ pt: 2 }}>
           <Box component="form" onSubmit={handleSendInitialMessage}>
             <TextField
               fullWidth
               label="Vaša poruka"
               variant="outlined"
               placeholder="Zdravo, zainteresovan sam za vaš oglas..."
               value={newMessage}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewMessage(e.target.value)}
               multiline
               rows={4}
               required
               autoFocus
               sx={{ mb: 2 }}
             />
           </Box>
         </DialogContent>
         
         <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
           <Button 
             onClick={handleCloseInitialMessageModal}
             variant="outlined"
             sx={{ px: 3, py: 1.5, minWidth: 100 }}
           >
             Otkaži
           </Button>
           <Button 
             onClick={handleSendInitialMessage}
             variant="contained"
             disabled={!newMessage.trim()}
             sx={{ px: 3, py: 1.5, minWidth: 100 }}
           >
             Pošalji
           </Button>
         </DialogActions>
       </Dialog>

       {/* Report User Dialog */}
       <Dialog
         open={reportDialogOpen}
         onClose={() => setReportDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>Prijavi korisnika</DialogTitle>
         <DialogContent>
           {reportError && (
             <Alert severity="error" sx={{ mb: 2 }}>
               {reportError}
             </Alert>
           )}
           {reportSuccess && (
             <Alert severity="success" sx={{ mb: 2 }}>
               {reportSuccess}
             </Alert>
           )}
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
             Prijavljujete korisnika: {selectedConversation && getOtherUserName(selectedConversation)}
           </Typography>
           <TextField
             fullWidth
             multiline
             rows={4}
             label="Razlog prijave"
             value={reportReason}
             onChange={(e) => setReportReason(e.target.value)}
             placeholder="Opišite zašto prijavljujete ovog korisnika..."
             disabled={reportLoading || !!reportSuccess}
           />
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
             Otkaži
           </Button>
           <Button
             onClick={handleReportSubmit}
             variant="contained"
             disabled={reportLoading || !!reportSuccess}
             sx={{
               backgroundColor: '#F3C015',
               color: C.onGold,
               '&:hover': {
                 backgroundColor: '#E0A21F',
               },
             }}
           >
             {reportLoading ? 'Prijavljivanje...' : 'Prijavi'}
           </Button>
         </DialogActions>
       </Dialog>
     </Container>
   );
 };

export default Messages; 